import 'leaflet/dist/leaflet.css';
import { BASEMAP_TILE_URL, BASEMAP_ATTRIBUTION } from '../../basemap.js';
import './map.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Slider from '@radix-ui/react-slider';
import { BookOpen, ChevronDown, ChevronUp, Filter, Search, Trophy, X } from 'lucide-react';
import { PanelEmptyState } from '../panel-empty-state.js';
import {
  AttributionControl,
  CircleMarker,
  MapContainer,
  Rectangle,
  TileLayer,
  useMap,
} from 'react-leaflet';
import { CANONICAL_SUBSEGMENTS, MARKET_NAMES, MARKETS, type MarketCode } from '@hc/shared';
import { PanelFrame } from '../panel-frame.js';
import { useActiveCountry, useGlobalCurrency, type PanelId, type TabId } from '../store.js';
import { useOpenMethodology } from '../use-open-methodology.js';
import { useCatchmentKey } from '../catchment-link.js';
import { useRollupLink } from '../rollup-link.js';
import { useMandateCountries } from '../use-mandate-defaults.js';
import { viewForMarkets } from '../market-geo.js';
import { CollapsibleFilterRow } from '../filter-modal-kit.js';
import {
  useConsolidators,
  useConsolidatorSites,
  useConsolidatorWhitespace,
} from '../../api/consolidators-hooks.js';
import { useQuery } from '@tanstack/react-query';
import { fetchConsolidatorCoverage, type ConsolidatorPlatform } from '../../api/client.js';
import { convertFromEur, CURRENCY_SYMBOL, type GlobalCurrency } from '../../currency.js';

// Consolidators — the "Who's Who" of the market. A league table of the named
// buy-and-build platforms (one row per resolved economic group) and the PE /
// strategic sponsors behind them. This is competitor intelligence: the
// consolidation landscape the Market/Opportunity panel measures in aggregate,
// drilled to the players. Fed by /consolidators; aggregate panel (no entity
// binding), reads the active country + the global display currency.
//
// Two lenses behind a toggle from one query:
//   • Platform — one row per operator/platform, sponsor shown.
//   • Sponsor  — one row per backer, expanding to the platforms it owns
//     (footprint summed). Self-owned strategics/listed (no named fund) are
//     their own owner — the operator IS the consolidator.
//
// Filters live in a modal (the Screener pattern): country, subsegment, and an
// estimated-scale range. Country + subsegment narrow the server aggregation;
// the scale range filters the returned platforms client-side (small set), so
// the summary + sponsor pivot reflect it instantly.

// The market picker lists ALL 27 and is gated by DATA, not by a hand-kept list.
//
// It used to render TARGET_MARKETS (11), disabling anything absent from MARKETS
// — a check that could never fail, because MARKETS is a superset of the 11. So
// the "soon" affordance was dead code and the real gate was the 11-item array,
// which hid 16 markets. Meanwhile some of the 11 have NO resolved groups at
// all, so they rendered an empty league table — and an empty league table reads
// as "nobody is consolidating here", which is the opposite of "we haven't
// resolved ownership here yet". A buyer sizing a roll-up would draw exactly the
// wrong conclusion.
//
// /consolidators/coverage now answers it from the database. Markets with no
// groups stay VISIBLE but disabled and labelled — hiding them would repeat the
// original mistake of making a data gap invisible.
const ALL_PICKER_MARKETS: ReadonlyArray<{ code: MarketCode; name: string }> = [...MARKETS]
  .map((code) => ({ code: code as MarketCode, name: MARKET_NAMES[code as MarketCode] ?? code }))
  .sort((a, b) => a.name.localeCompare(b.name));

type Lens = 'platform' | 'sponsor';

// Backer-type → short label. Neutral chip in the table — the kind of capital.
const BACKER_LABEL: Record<string, string> = {
  pe: 'PE',
  infra: 'Infra',
  sovereign: 'Sovereign',
  strategic: 'Strategic',
  listed: 'Listed',
  reit: 'REIT',
  family: 'Family',
  cooperative: 'Co-op',
  foundation: 'Foundation',
};
// Backer-type → capital GROUP. A buy-sider reasons in three kinds of capital,
// not nine labels: will this owner sell (financial), compete (strategic), or
// sit forever (family/mutual)? The map colours + the filter work on these three
// groups; the table still shows the precise per-type label (BACKER_LABEL).
const BACKER_GROUPS = [
  {
    key: 'financial',
    label: 'Financial sponsor',
    color: '#a8572a', // brand terracotta — the active, return-driven capital
    types: ['pe', 'infra', 'sovereign', 'reit'],
  },
  {
    key: 'strategic',
    label: 'Strategic / listed',
    color: '#990f3d', // claret — the mid step of the warm→cool capital gradient
    types: ['strategic', 'listed'],
  },
  {
    key: 'family',
    label: 'Family / mutual',
    color: '#0d7680', // teal — the cool end (patient / passive capital)
    types: ['family', 'cooperative', 'foundation'],
  },
] as const;
const TYPE_TO_GROUP: Record<string, string> = {};
for (const g of BACKER_GROUPS) for (const t of g.types) TYPE_TO_GROUP[t] = g.key;
const GROUP_COLOR: Record<string, string> = Object.fromEntries(
  BACKER_GROUPS.map((g) => [g.key, g.color]),
);
const GROUP_LABEL: Record<string, string> = Object.fromEntries(
  BACKER_GROUPS.map((g) => [g.key, g.label]),
);
const GROUP_ORDER = BACKER_GROUPS.map((g) => g.key);

// The fourth colour on this map, and by far the most common one: a resolved
// economic group whose BACKER we have not identified. It is a statement about
// our coverage, not a fourth kind of capital — which is exactly why it has to
// be named in the legend. Unlabelled grey reads as a category the viewer failed
// to notice, rather than as a gap we already know about. As of 2026-07-23 it is
// 90.7% of the bubbles (only 54 of 3,663 platforms resolve to a sponsor), so
// leaving it out meant the legend explained under a tenth of what is drawn.
const UNRESOLVED_COLOR = '#9a948a';
function groupColorForType(t: string | null | undefined): string {
  return (t && GROUP_COLOR[TYPE_TO_GROUP[t]!]) || UNRESOLVED_COLOR;
}

// Map centring per market (metropolitan centroid + zoom).
// Market framing lives in the shared market-geo table (all 27 markets +
// the Europe frame) — see viewForMarkets.

// Bubble size = discrete tiers by platform est. scale. Discrete reads far more
// legibly at country zoom than a continuous √ (which compresses the middle).
// The size legend renders these same tiers.
const SIZE_TIERS: ReadonlyArray<{ max: number; r: number; label: string }> = [
  { max: 10e6, r: 3, label: '< €10M' },
  { max: 50e6, r: 5, label: '€10–50M' },
  { max: 250e6, r: 8, label: '€50–250M' },
  { max: Infinity, r: 12, label: '€250M+' },
];
function tierRadius(rev: number): number {
  for (const t of SIZE_TIERS) if (rev < t.max) return t.r;
  return SIZE_TIERS[SIZE_TIERS.length - 1]!.r;
}

// Estimated-scale ladder (EUR) — non-linear, dense at the small end where most
// platforms sit. First stop = no minimum, last = no maximum (sentinels).
const SCALE_STOPS: readonly number[] = [
  0, 500_000, 1_000_000, 2_000_000, 5_000_000, 10_000_000, 25_000_000, 50_000_000, 100_000_000,
  250_000_000, 500_000_000,
];
const SCALE_MIN = SCALE_STOPS[0]!;
const SCALE_MAX = SCALE_STOPS[SCALE_STOPS.length - 1]!;
type ScaleRange = [number, number];

function fmtMoney(amountEur: number, ccy: GlobalCurrency, decimals = 1): string {
  const v = convertFromEur(amountEur, ccy);
  const sym = CURRENCY_SYMBOL[ccy];
  const a = Math.abs(v);
  let body: string;
  if (a >= 1e9) body = (v / 1e9).toFixed(decimals) + 'B';
  else if (a >= 1e6) body = (v / 1e6).toFixed(decimals) + 'M';
  else if (a >= 1e3) body = (v / 1e3).toFixed(0) + 'K';
  else body = String(Math.round(v));
  return sym + body;
}
const fmtN = (v: number) => v.toLocaleString('en-GB');

// Opportunity heat ramp — cream → terracotta accent, the SAME ramp as the
// Market Opportunity panel, so a hot white-space cell reads identically there.
const clamp01 = (t: number) => Math.min(1, Math.max(0, t));
function oppRamp(t: number): string {
  t = clamp01(t);
  const a = [255, 241, 229];
  const b = [168, 87, 42];
  const c = a.map((x, i) => Math.round(x + (b[i]! - x) * t));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

// ---- sponsor-first pivot --------------------------------------------------

interface SponsorRow {
  /** Owner display key — the named backer, else the platform name when its
   *  fund isn't in the crosswalk (backer unknown ≠ self-owned). */
  owner: string;
  backerType: string | null;
  platforms: ConsolidatorPlatform[];
  opcos: number;
  sites: number;
  revenueEur: number;
  subsegments: string[];
  regions: string[];
  /** True when every platform under this owner is curated (no financials). */
  curated: boolean;
}

function pivotToSponsors(platforms: ConsolidatorPlatform[]): SponsorRow[] {
  const by = new Map<string, SponsorRow>();
  for (const p of platforms) {
    // A named backer groups its platforms together (EQT → IVC + Medivet). When
    // the fund isn't named in the crosswalk the platform stands as its own row,
    // keyed by group name — it's backed-but-unattributed, not self-owned; the
    // Type chip still carries PE / Strategic / Listed.
    const key = p.sponsor ?? p.group;
    let row = by.get(key);
    if (!row) {
      row = {
        owner: key,
        backerType: p.backerType,
        platforms: [],
        opcos: 0,
        sites: 0,
        revenueEur: 0,
        subsegments: [],
        regions: [],
        curated: true,
      };
      by.set(key, row);
    }
    row.platforms.push(p);
    row.curated = row.curated && !!p.curated;
    row.opcos += p.opcos;
    row.sites += p.sites;
    row.revenueEur += p.revenueEur;
    for (const s of p.subsegments) if (!row.subsegments.includes(s)) row.subsegments.push(s);
    for (const r of p.regions) if (!row.regions.includes(r)) row.regions.push(r);
  }
  return [...by.values()].map((r) => ({
    ...r,
    subsegments: r.subsegments.sort(),
    regions: r.regions.sort(),
  }));
}

// ---- sorting --------------------------------------------------------------

type SortKey = 'name' | 'opcos' | 'sites' | 'regions' | 'platforms' | 'revenue';
type SortDir = 'asc' | 'desc';

function platformSortVal(p: ConsolidatorPlatform, k: SortKey): number | string {
  switch (k) {
    case 'name':
      return p.group.toLowerCase();
    case 'opcos':
      return p.opcos;
    case 'sites':
      return p.sites;
    case 'regions':
      return p.regions.length;
    case 'revenue':
      return p.revenueEur;
    default:
      return p.revenueEur;
  }
}
function sponsorSortVal(s: SponsorRow, k: SortKey): number | string {
  switch (k) {
    case 'name':
      return s.owner.toLowerCase();
    case 'opcos':
      return s.opcos;
    case 'sites':
      return s.sites;
    case 'platforms':
      return s.platforms.length;
    case 'revenue':
      return s.revenueEur;
    default:
      return s.revenueEur;
  }
}
function cmp(a: number | string, b: number | string, dir: SortDir): number {
  const r = a < b ? -1 : a > b ? 1 : 0;
  return dir === 'asc' ? r : -r;
}

// ---- small presentational bits (defined above the panel for react-refresh) -

function BackerChip({ type }: { type: string | null }) {
  if (!type) return <span className="cn-dim">—</span>;
  return <span className="cn-type">{BACKER_LABEL[type] ?? type}</span>;
}

function SubChips({ subs }: { subs: string[] }) {
  if (subs.length === 0) return <span className="cn-dim">—</span>;
  // Plain comma-separated text (not pills).
  return <span className="cn-subs-text">{subs.join(', ')}</span>;
}

function SortHead({
  label,
  k,
  numeric,
  sortKey,
  sortDir,
  onSort,
}: {
  label: string;
  k?: SortKey;
  numeric?: boolean;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (k: SortKey) => void;
}) {
  const active = k && sortKey === k;
  return (
    <th
      className={(numeric ? 'cn-r' : 'cn-l') + (k ? ' cn-sortable' : '') + (active ? ' on' : '')}
      onClick={k ? () => onSort(k) : undefined}
    >
      <span className="cn-th-inner">
        {label}
        {active && (sortDir === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />)}
      </span>
    </th>
  );
}

export function ConsolidatorsPanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  const activeCountry = useActiveCountry();
  const ccy = useGlobalCurrency();
  // Market scope — seeding priority: workspace active-country filter (explicit
  // user choice) → the buyer's mandated markets → Europe-wide (every market
  // with resolved ownership groups). Never a hardcoded UK default. Seeding
  // waits for the mandate + coverage queries (both cached/deduped), so the
  // panel shows its loading state briefly rather than flashing a wrong market.
  const [countries, setCountries] = useState<MarketCode[]>([]);
  const { countries: mandateCountries, loaded: mandatesLoaded } = useMandateCountries();
  const coverage = useQuery({
    queryKey: ['consolidators', 'coverage'],
    queryFn: fetchConsolidatorCoverage,
    staleTime: 30 * 60_000,
  });
  // What this panel opens on when the user has expressed nothing — kept so the
  // "Country" filter only reads as active once the user deviates from it.
  const defaultCountries = useRef<MarketCode[]>([]);
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !mandatesLoaded || !coverage.data) return;
    seededRef.current = true;
    const covered = new Set(
      coverage.data.markets.filter((m) => m.hasData).map((m) => m.country as MarketCode),
    );
    const mandated = mandateCountries.filter((c) => covered.has(c));
    const next =
      activeCountry && covered.has(activeCountry as MarketCode)
        ? [activeCountry as MarketCode]
        : mandated.length > 0
          ? mandated
          : [...covered];
    defaultCountries.current = next;
    setCountries(next);
  }, [mandatesLoaded, mandateCountries, coverage.data, activeCountry]);
  const [view, setView] = useState<'table' | 'map'>('map');
  const [lens, setLens] = useState<Lens>('platform');
  const [subsegments, setSubsegments] = useState<string[]>([]);
  const [backerGroups, setBackerGroups] = useState<string[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [scale, setScale] = useState<ScaleRange>([SCALE_MIN, SCALE_MAX]);
  const [whitespace, setWhitespace] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('revenue');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [wsAdviseOpen, setWsAdviseOpen] = useState(false);
  // A Roll-Up Foundation on this channel can offer to focus white-space on its
  // combine's subsegment (opt-in — never overrides the user's filters silently).
  const rollupKey = useCatchmentKey(tabId, panelId);
  const rollupLens = useRollupLink((s) => s.byKey[rollupKey] ?? null);
  const openMethodology = useOpenMethodology();

  // Selected capital-groups → the individual backer types they cover (what the
  // server + the platform filter actually match on).
  const selectedBackerTypes = useMemo(
    () => BACKER_GROUPS.filter((g) => backerGroups.includes(g.key)).flatMap((g) => g.types),
    [backerGroups],
  );

  const scaleActive = scale[0] > SCALE_MIN || scale[1] < SCALE_MAX;
  const countryActive =
    countries.length !== defaultCountries.current.length ||
    countries.some((c) => !defaultCountries.current.includes(c));
  const activeFilterCount =
    (subsegments.length > 0 ? 1 : 0) +
    (scaleActive ? 1 : 0) +
    (countryActive ? 1 : 0) +
    (backerGroups.length > 0 ? 1 : 0) +
    (groups.length > 0 ? 1 : 0);

  const query = useMemo(
    () => ({ country: countries, subsegment: subsegments.length ? subsegments : undefined }),
    [countries, subsegments],
  );
  // countries === [] only during the seeding window — hold the fetches until
  // the scope is known so we never issue a throwaway all-markets query.
  const scopeReady = countries.length > 0;
  const { data, isLoading, isError } = useConsolidators(query, { enabled: scopeReady });

  // Site points for the Map view — only fetched once the map is open.
  const sitesQuery = useMemo(
    () => ({
      country: countries,
      subsegment: subsegments.length ? subsegments : undefined,
      backerType: selectedBackerTypes.length ? selectedBackerTypes : undefined,
    }),
    [countries, subsegments, selectedBackerTypes],
  );
  const { data: sitesData, isLoading: sitesLoading } = useConsolidatorSites(sitesQuery, {
    enabled: view === 'map' && scopeReady,
  });

  // White space is only meaningful WITHIN a subsegment — "independents with no
  // consolidator" comparing dental independents to vet consolidators is
  // nonsense. So it requires a subsegment selection; the toggle is disabled and
  // the layer empty otherwise.
  const whitespaceReady = subsegments.length > 0;
  const wsQuery = useMemo(
    () => ({ country: countries, subsegment: subsegments }),
    [countries, subsegments],
  );
  const { data: wsData } = useConsolidatorWhitespace(wsQuery, {
    enabled: view === 'map' && whitespace && whitespaceReady && scopeReady,
  });

  // Scale + capital-group + name filters client-side over the platform set.
  const platforms = useMemo(() => {
    const all = data?.platforms ?? [];
    const filtered = all.filter(
      (p) =>
        p.revenueEur >= scale[0] &&
        (scale[1] >= SCALE_MAX || p.revenueEur <= scale[1]) &&
        (backerGroups.length === 0 ||
          (p.backerType && backerGroups.includes(TYPE_TO_GROUP[p.backerType]!))) &&
        (groups.length === 0 || groups.includes(p.group)),
    );
    return filtered
      .slice()
      .sort((a, b) => cmp(platformSortVal(a, sortKey), platformSortVal(b, sortKey), sortDir));
  }, [data, scale, backerGroups, groups, sortKey, sortDir]);

  // All platform names (unfiltered) for the searchable name dropdown.
  const allGroups = useMemo(
    () =>
      (data?.platforms ?? [])
        .map((p) => p.group)
        .slice()
        .sort((a, b) => a.localeCompare(b)),
    [data],
  );

  // Bubble radius per group = discrete size tier by the platform's est. scale,
  // so every clinic of a platform reads at its owner's size class and the tiers
  // are legible against the size legend.
  const radiusByGroup = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of platforms) m.set(p.group, tierRadius(p.revenueEur));
    return m;
  }, [platforms]);

  // Map sites narrowed to the selected platform names (client-side).
  const mapSites = useMemo(() => {
    const s = sitesData?.sites ?? [];
    return groups.length ? s.filter((x) => groups.includes(x.group)) : s;
  }, [sitesData, groups]);

  // Territory-grid white space — a uniform-cell rule that generalises across
  // markets (presence-based, no fixed-km radius). A cell is white space when it
  // holds independents but NO consolidator of the currently-shown set; shaded
  // by independent count = size of the prize. Consolidator presence is taken
  // from the full mapSites (exact), independents from the server cell counts.
  const whitespaceCells = useMemo(() => {
    if (!whitespace || !whitespaceReady) return [];
    const cs = wsData?.cellDeg ?? 0.25;
    const idx = wsData?.regionIndex ?? {};
    const cons = new Set<string>();
    for (const s of mapSites) cons.add(`${Math.floor(s.lat / cs)}|${Math.floor(s.lng / cs)}`);
    return (wsData?.cells ?? [])
      .filter((c) => !cons.has(`${c.ci}|${c.cj}`))
      .map((c) => ({
        n: c.n,
        // Market-Opportunity index of the cell's region (how attractive the
        // market is), null when the region is too thin to score.
        index: c.region ? (idx[c.region] ?? null) : null,
        bounds: [
          [c.ci * cs, c.cj * cs],
          [(c.ci + 1) * cs, (c.cj + 1) * cs],
        ] as [[number, number], [number, number]],
      }));
  }, [whitespace, whitespaceReady, wsData, mapSites]);

  const sponsors = useMemo(() => {
    const rows = pivotToSponsors(platforms);
    return rows.sort((a, b) =>
      cmp(sponsorSortVal(a, sortKey), sponsorSortVal(b, sortKey), sortDir),
    );
  }, [platforms, sortKey, sortDir]);

  const summary = useMemo(() => {
    const totalRev = platforms.reduce((a, p) => a + p.revenueEur, 0);
    const totalSites = platforms.reduce((a, p) => a + p.sites, 0);
    const named = new Set(platforms.filter((p) => p.sponsor).map((p) => p.sponsor!));
    return { nPlatforms: platforms.length, nSponsors: named.size, totalRev, totalSites };
  }, [platforms]);

  // Capital groups present in the unfiltered result — drives the modal's filter
  // options + the map legend (only show groups that actually appear).
  const groupsPresent = useMemo(() => {
    const set = new Set<string>();
    for (const p of data?.platforms ?? [])
      if (p.backerType && TYPE_TO_GROUP[p.backerType]) set.add(TYPE_TO_GROUP[p.backerType]!);
    return GROUP_ORDER.filter((g) => set.has(g));
  }, [data]);

  function onSort(k: SortKey) {
    if (k === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir(k === 'name' ? 'asc' : 'desc');
    }
  }

  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <div className="cn-root">
        <style>{CN_CSS}</style>

        <div className="cn-toolbar">
          <div className="cn-seg" role="group" aria-label="View">
            <button
              type="button"
              className={view === 'table' ? 'on' : ''}
              onClick={() => setView('table')}
              title="League table"
            >
              Table
            </button>
            <button
              type="button"
              className={view === 'map' ? 'on' : ''}
              onClick={() => setView('map')}
              title="Footprint map — clinic locations"
            >
              Map
            </button>
          </div>
          {view === 'table' && (
            <div className="cn-seg" role="group" aria-label="Lens">
              <button
                type="button"
                className={lens === 'platform' ? 'on' : ''}
                onClick={() => setLens('platform')}
                title="One row per platform"
              >
                Platforms
              </button>
              <button
                type="button"
                className={lens === 'sponsor' ? 'on' : ''}
                onClick={() => setLens('sponsor')}
                title="One row per backer"
              >
                Sponsors
              </button>
            </div>
          )}
          <button
            type="button"
            className={'cn-filterbtn' + (activeFilterCount > 0 ? ' active' : '')}
            onClick={() => setFiltersOpen(true)}
            aria-haspopup="dialog"
          >
            <Filter size={12} />
            <span>Filters</span>
            {activeFilterCount > 0 && <span className="cn-fcount">· {activeFilterCount}</span>}
          </button>
          {view === 'map' && (
            <button
              type="button"
              className="cn-filterbtn"
              onClick={() => (whitespaceReady ? setWhitespace((w) => !w) : setWsAdviseOpen(true))}
              title="Territories with independents but no consolidator — the bolt-on white space"
            >
              <span>Show white space</span>
              {whitespace && whitespaceReady && whitespaceCells.length > 0 && (
                <span className="cn-fcount">· {fmtN(whitespaceCells.length)}</span>
              )}
            </button>
          )}
          {rollupLens?.subsegment && (
            <button
              type="button"
              className="cn-filterbtn"
              title={`Focus white space on the roll-up's ${rollupLens.subsegment} market`}
              onClick={() => {
                if (!rollupLens?.subsegment) return;
                setSubsegments([rollupLens.subsegment]);
                if (rollupLens.country) setCountries([rollupLens.country as MarketCode]);
                setWhitespace(true);
                setView('map');
              }}
            >
              Roll-up: {rollupLens.subsegment}
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="cn-filterbtn"
            title="How White Space is estimated and plotted"
            onClick={() => openMethodology('whitespace')}
          >
            <BookOpen size={12} /> Methodology
          </button>
          <span className="cn-count">
            {isLoading || !scopeReady
              ? '…'
              : view === 'map'
                ? `${fmtN(summary.nPlatforms)} platforms · ${fmtN(summary.totalSites)} sites · ${fmtMoney(summary.totalRev, ccy)}`
                : `${fmtN(summary.nPlatforms)} platforms · ${fmtMoney(summary.totalRev, ccy)}`}
          </span>
        </div>

        {view === 'map' ? (
          <ConsolidatorMap
            sites={mapSites}
            loading={sitesLoading || !scopeReady}
            countries={countries}
            groupsPresent={groupsPresent}
            radiusByGroup={radiusByGroup}
            whitespace={whitespace && whitespaceReady}
            whitespaceCells={whitespaceCells}
          />
        ) : (
          <div className="cn-tablewrap">
            {isError ? (
              <div className="cn-empty">Couldn’t load consolidator data.</div>
            ) : isLoading || !scopeReady ? (
              <div className="cn-empty">Loading…</div>
            ) : platforms.length === 0 ? (
              <PanelEmptyState fill icon={<Trophy size={18} />} title="No consolidators in view">
                No roll-up groups resolve under these filters yet. Widen the sub-segment, region or
                size band — the competitor league table ranks the active buyers as soon as any
                match.
              </PanelEmptyState>
            ) : lens === 'platform' ? (
              <table className="cn-tbl">
                <thead>
                  <tr>
                    <SortHead label="Platform" k="name" {...{ sortKey, sortDir, onSort }} />
                    <SortHead label="Backer" {...{ sortKey, sortDir, onSort }} />
                    <SortHead label="Type" {...{ sortKey, sortDir, onSort }} />
                    <SortHead label="Subsegments" {...{ sortKey, sortDir, onSort }} />
                    <SortHead label="Opcos" k="opcos" numeric {...{ sortKey, sortDir, onSort }} />
                    <SortHead label="Sites" k="sites" numeric {...{ sortKey, sortDir, onSort }} />
                    <SortHead
                      label="Regions"
                      k="regions"
                      numeric
                      {...{ sortKey, sortDir, onSort }}
                    />
                    <SortHead
                      label="Est. scale"
                      k="revenue"
                      numeric
                      {...{ sortKey, sortDir, onSort }}
                    />
                  </tr>
                </thead>
                <tbody>
                  {platforms.map((p) => (
                    <tr key={p.group}>
                      <td className="cn-l cn-name">{p.group}</td>
                      <td className="cn-l cn-sponsor">
                        {p.sponsor ?? <span className="cn-dim">—</span>}
                      </td>
                      <td className="cn-l">
                        <BackerChip type={p.backerType} />
                      </td>
                      <td className="cn-l">
                        <SubChips subs={p.subsegments} />
                      </td>
                      <td className="cn-r">
                        {p.curated ? <span className="cn-dim">—</span> : fmtN(p.opcos)}
                      </td>
                      <td className="cn-r">
                        {p.sites ? fmtN(p.sites) : <span className="cn-dim">—</span>}
                      </td>
                      <td className="cn-r">{fmtN(p.regions.length)}</td>
                      <td className="cn-r cn-rev">
                        {p.curated ? (
                          <span className="cn-dim">—</span>
                        ) : (
                          fmtMoney(p.revenueEur, ccy)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="cn-tbl">
                <thead>
                  <tr>
                    <SortHead label="Sponsor / owner" k="name" {...{ sortKey, sortDir, onSort }} />
                    <SortHead label="Type" {...{ sortKey, sortDir, onSort }} />
                    <SortHead
                      label="Platforms"
                      k="platforms"
                      numeric
                      {...{ sortKey, sortDir, onSort }}
                    />
                    <SortHead label="Names" {...{ sortKey, sortDir, onSort }} />
                    <SortHead label="Opcos" k="opcos" numeric {...{ sortKey, sortDir, onSort }} />
                    <SortHead label="Sites" k="sites" numeric {...{ sortKey, sortDir, onSort }} />
                    <SortHead
                      label="Est. scale"
                      k="revenue"
                      numeric
                      {...{ sortKey, sortDir, onSort }}
                    />
                  </tr>
                </thead>
                <tbody>
                  {sponsors.map((s) => (
                    <tr key={s.owner}>
                      <td className="cn-l cn-name">{s.owner}</td>
                      <td className="cn-l">
                        <BackerChip type={s.backerType} />
                      </td>
                      <td className="cn-r">
                        {s.platforms.length > 1 ? <b>{s.platforms.length}</b> : s.platforms.length}
                      </td>
                      <td
                        className="cn-l cn-platnames"
                        title={s.platforms.map((p) => p.group).join(', ')}
                      >
                        {s.platforms.map((p) => p.group).join(', ')}
                      </td>
                      <td className="cn-r">
                        {s.curated ? <span className="cn-dim">—</span> : fmtN(s.opcos)}
                      </td>
                      <td className="cn-r">
                        {s.sites ? fmtN(s.sites) : <span className="cn-dim">—</span>}
                      </td>
                      <td className="cn-r cn-rev">
                        {s.curated ? (
                          <span className="cn-dim">—</span>
                        ) : (
                          fmtMoney(s.revenueEur, ccy)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <Dialog.Root open={wsAdviseOpen} onOpenChange={setWsAdviseOpen}>
          <Dialog.Portal>
            <Dialog.Overlay
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9499 }}
            />
            <Dialog.Content
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 'min(360px, 92vw)',
                background: 'var(--surface)',
                border: '1px solid var(--hairline)',
                borderRadius: 4,
                zIndex: 9500,
                padding: 'var(--s-4)',
              }}
            >
              <Dialog.Title
                style={{ margin: '0 0 8px', fontSize: 'var(--t-section)', fontWeight: 600 }}
              >
                Pick a sub-segment first
              </Dialog.Title>
              <Dialog.Description
                style={{
                  margin: '0 0 16px',
                  fontSize: 'var(--t-body)',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.5,
                }}
              >
                White space is calculated per sub-segment (dental gaps aren&apos;t vet gaps), so
                select at least one sub-segment in Filters before showing it.
              </Dialog.Description>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  type="button"
                  className="cn-filterbtn"
                  onClick={() => {
                    setWsAdviseOpen(false);
                    setFiltersOpen(true);
                  }}
                >
                  Open filters
                </button>
                <Dialog.Close asChild>
                  <button type="button" className="cn-filterbtn">
                    Got it
                  </button>
                </Dialog.Close>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        <FilterModal
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          countries={countries}
          countryActive={countryActive}
          setCountries={setCountries}
          subsegments={subsegments}
          setSubsegments={setSubsegments}
          backerGroups={backerGroups}
          setBackerGroups={setBackerGroups}
          groupsPresent={groupsPresent}
          groups={groups}
          setGroups={setGroups}
          allGroups={allGroups}
          scale={scale}
          setScale={setScale}
          ccy={ccy}
          onReset={() => {
            setSubsegments([]);
            setBackerGroups([]);
            setGroups([]);
            setScale([SCALE_MIN, SCALE_MAX]);
          }}
        />
      </div>
    </PanelFrame>
  );
}

// ---- map view --------------------------------------------------------------

/** Keep Leaflet sized to the dockview panel + recenter when the market changes. */
function MapSync({ center, zoom }: { center: [number, number]; zoom: number }) {
  const map = useMap();
  useEffect(() => {
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(map.getContainer());
    const t = setTimeout(() => map.invalidateSize(), 60);
    return () => {
      ro.disconnect();
      clearTimeout(t);
    };
  }, [map]);
  useEffect(() => {
    map.setView(center, zoom);
  }, [map, center[0], center[1], zoom]);
  return null;
}

type Site = {
  lat: number;
  lng: number;
  group: string;
  subsegment: string | null;
  backerType: string | null;
};

function ConsolidatorMap({
  sites,
  loading,
  countries,
  groupsPresent,
  radiusByGroup,
  whitespace,
  whitespaceCells,
}: {
  sites: Site[];
  loading: boolean;
  countries: MarketCode[];
  groupsPresent: string[];
  radiusByGroup: Map<string, number>;
  whitespace: boolean;
  whitespaceCells: Array<{
    n: number;
    index: number | null;
    bounds: [[number, number], [number, number]];
  }>;
}) {
  const [sel, setSel] = useState<Site | null>(null);
  const unresolvedSites = useMemo(
    () => sites.reduce((n, s) => n + (s.backerType && TYPE_TO_GROUP[s.backerType] ? 0 : 1), 0),
    [sites],
  );
  const cv = viewForMarkets(countries);
  const wsMax = whitespaceCells.reduce((a, c) => Math.max(a, c.n), 1);

  return (
    <div className="cn-mapwrap">
      {sel && (
        <div className="cn-sitecard">
          <button
            type="button"
            className="cn-sitecard-x"
            onClick={() => setSel(null)}
            aria-label="Close"
          >
            <X size={12} />
          </button>
          <div className="cn-sitecard-grp">{sel.group}</div>
          <div className="cn-sitecard-meta">
            {/* Always swatch — including the unresolved case, so the dot the
                user just clicked is traceable to a legend entry. */}
            <span className="cn-swatch" style={{ background: groupColorForType(sel.backerType) }} />
            {sel.backerType
              ? (BACKER_LABEL[sel.backerType] ?? sel.backerType)
              : 'Backer unresolved'}
            {sel.subsegment ? ` · ${sel.subsegment}` : ''}
          </div>
        </div>
      )}

      <div className="cn-maplegend">
        {/* capital-group key (3 groups, not 9 types) */}
        {groupsPresent.map((g) => (
          <span key={g} className="cn-legitem">
            <span className="cn-swatch" style={{ background: GROUP_COLOR[g] }} />
            {GROUP_LABEL[g] ?? g}
          </span>
        ))}
        {/* Keyed off the SITES actually drawn, not off the platform list: the
            legend must describe this map, and the two differ once a backer
            filter is applied. Shown last — it is the residual, not a peer of
            the three capital groups. */}
        {unresolvedSites > 0 && (
          <span
            className="cn-legitem"
            title={
              `${unresolvedSites.toLocaleString()} of ${sites.length.toLocaleString()} sites belong to a ` +
              `resolved group whose owner we have not identified. A coverage gap, not a kind of capital.`
            }
          >
            <span className="cn-swatch" style={{ background: UNRESOLVED_COLOR }} />
            Backer unresolved
            <span className="cn-legsize">
              {Math.round((100 * unresolvedSites) / Math.max(sites.length, 1))}%
            </span>
          </span>
        )}
        {whitespace && (
          <span
            className="cn-legitem"
            title="White space shaded by Market-Opportunity index of the region"
          >
            White space
            <span
              className="cn-gradbar"
              style={{
                background: `linear-gradient(90deg, ${oppRamp(0)}, ${oppRamp(0.55)}, ${oppRamp(1)})`,
              }}
            />
            opportunity
          </span>
        )}
        {/* size key — bubble radius = platform est. scale tier */}
        <span className="cn-legsep" />
        {SIZE_TIERS.map((t) => (
          <span key={t.label} className="cn-legitem cn-legsize">
            <span
              className="cn-sizedot"
              style={{ width: t.r * 2, height: t.r * 2, marginRight: 4 + (12 - t.r) }}
            />
            {t.label}
          </span>
        ))}
      </div>

      {loading && <div className="cn-maploading">Loading clinic locations…</div>}

      <div className="hc-map-root">
        <MapContainer
          center={cv.center}
          zoom={cv.zoom}
          minZoom={4}
          maxZoom={18}
          preferCanvas
          scrollWheelZoom
          attributionControl={false}
        >
          <AttributionControl prefix={false} position="bottomright" />
          <TileLayer
            className="hc-map-tiles"
            url={BASEMAP_TILE_URL}
            attribution={BASEMAP_ATTRIBUTION}
            subdomains="abcd"
            maxZoom={19}
          />
          <MapSync center={cv.center} zoom={cv.zoom} />

          {/* white-space layer — territories with independents but NO
            consolidator. COLOUR = the region's Market-Opportunity index (hot =
            attractive market), OPACITY = local independent count (size of the
            prize). So a strong cell is unclaimed AND attractive AND dense. */}
          {whitespace &&
            whitespaceCells.map((c, i) => (
              <Rectangle
                key={`w${i}`}
                bounds={c.bounds}
                interactive={false}
                pathOptions={{
                  stroke: false,
                  fillColor: c.index == null ? '#b8ae9e' : oppRamp(c.index / 100),
                  fillOpacity: 0.3 + 0.45 * (c.n / wsMax),
                }}
              />
            ))}

          {/* consolidator bubbles — sized by est.-scale tier, coloured by capital */}
          {sites.map((s, i) => {
            const col = groupColorForType(s.backerType);
            return (
              <CircleMarker
                key={i}
                center={[s.lat, s.lng]}
                radius={radiusByGroup.get(s.group) ?? 3}
                bubblingMouseEvents={false}
                pathOptions={{
                  color: col,
                  weight: 0.4,
                  fillColor: col,
                  fillOpacity: 0.45,
                }}
                eventHandlers={{ click: () => setSel(s) }}
              />
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}

// ---- filter modal (Screener pattern) --------------------------------------

function FilterModal({
  open,
  onOpenChange,
  countries,
  countryActive,
  setCountries,
  subsegments,
  setSubsegments,
  backerGroups,
  setBackerGroups,
  groupsPresent,
  groups,
  setGroups,
  allGroups,
  scale,
  setScale,
  ccy,
  onReset,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  countries: MarketCode[];
  /** True when the market scope differs from the panel's seeded default
   *  (mandate / Europe-wide) — drives the row's "active" highlight. */
  countryActive: boolean;
  setCountries: (c: MarketCode[]) => void;
  subsegments: string[];
  setSubsegments: (s: string[]) => void;
  backerGroups: string[];
  setBackerGroups: (b: string[]) => void;
  groupsPresent: string[];
  groups: string[];
  setGroups: (g: string[]) => void;
  allGroups: string[];
  scale: ScaleRange;
  setScale: (s: ScaleRange) => void;
  ccy: GlobalCurrency;
  onReset: () => void;
}) {
  const [groupSearch, setGroupSearch] = useState('');

  // Per-market group depth — gates which markets are selectable below. Lives
  // here rather than in the panel because the picker is the only consumer;
  // react-query dedupes it against any other holder of the same key. Cached
  // hard: it only moves when ownership resolution re-runs (weekly).
  const coverage = useQuery({
    queryKey: ['consolidators', 'coverage'],
    queryFn: fetchConsolidatorCoverage,
    staleTime: 30 * 60_000,
  });
  const coverageByCountry = useMemo(
    () => new Map((coverage.data?.markets ?? []).map((m) => [m.country as MarketCode, m])),
    [coverage.data],
  );

  function toggleSub(s: string) {
    setSubsegments(
      subsegments.includes(s) ? subsegments.filter((x) => x !== s) : [...subsegments, s],
    );
  }
  function toggleGroup(g: string) {
    setGroups(groups.includes(g) ? groups.filter((x) => x !== g) : [...groups, g]);
  }
  const groupMatches = allGroups.filter((g) =>
    g.toLowerCase().includes(groupSearch.trim().toLowerCase()),
  );
  function toggleBacker(b: string) {
    setBackerGroups(
      backerGroups.includes(b) ? backerGroups.filter((x) => x !== b) : [...backerGroups, b],
    );
  }
  function toggleCountry(c: MarketCode) {
    if (countries.includes(c)) {
      if (countries.length > 1) setCountries(countries.filter((x) => x !== c)); // keep ≥1
    } else {
      setCountries([...countries, c]);
    }
  }
  const scaleLabel = (idx: 0 | 1) => {
    const v = scale[idx];
    if (idx === 0 && v <= SCALE_MIN) return 'Any';
    if (idx === 1 && v >= SCALE_MAX) return 'No max';
    return fmtMoney(v, ccy, 0);
  };
  const idxOf = (v: number) => {
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < SCALE_STOPS.length; i++) {
      const d = Math.abs(SCALE_STOPS[i]! - v);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best;
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9499 }}
        />
        <Dialog.Content
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(460px, 92vw)',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--surface)',
            border: '1px solid var(--hairline)',
            borderRadius: 4,
            zIndex: 9500,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '36px 1fr 36px',
              alignItems: 'center',
              padding: 'var(--s-3)',
              borderBottom: '1px solid var(--hairline)',
            }}
          >
            <span />
            <Dialog.Title
              style={{
                margin: 0,
                fontSize: 'var(--t-section)',
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              Filters
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: 2,
                  color: 'var(--text-secondary)',
                  justifySelf: 'end',
                }}
              >
                <X size={14} />
              </button>
            </Dialog.Close>
          </div>

          <div style={{ flex: 1, overflow: 'auto', padding: 'var(--s-3)' }}>
            {/* Country — full 11-market roster, multi-select. Markets not yet
                in the data model are disabled ("soon"). */}
            <CollapsibleFilterRow
              label="Country"
              summary={
                countries.length === 0
                  ? 'All'
                  : countries.length === 1
                    ? (MARKET_NAMES[countries[0]!] ?? countries[0]!)
                    : `${countries.length} selected`
              }
              active={countryActive}
            >
              <div className="cn-checklist">
                {ALL_PICKER_MARKETS.map((m) => {
                  // Undefined while the facet loads → treat as selectable, so
                  // the picker never flickers everything to disabled.
                  const cov = coverageByCountry.get(m.code);
                  const selectable = cov?.hasData ?? true;
                  const on = countries.includes(m.code);
                  return (
                    <button
                      key={m.code}
                      type="button"
                      role="checkbox"
                      aria-checked={on}
                      disabled={!selectable}
                      className={'cn-check' + (on ? ' on' : '') + (selectable ? '' : ' soon')}
                      title={
                        selectable
                          ? cov
                            ? `${cov.groups.toLocaleString()} resolved group${cov.groups === 1 ? '' : 's'}`
                            : undefined
                          : 'No ownership groups resolved in this market yet — this is a data gap, not an absence of consolidation.'
                      }
                      onClick={() => selectable && toggleCountry(m.code)}
                    >
                      <span className="cn-checkbox">{on ? '✓' : ''}</span>
                      <span>{m.name}</span>
                      {!selectable && <span className="cn-soon">no data</span>}
                    </button>
                  );
                })}
              </div>
            </CollapsibleFilterRow>

            {/* Capital group (the kind of capital behind the platform) */}
            {groupsPresent.length > 0 && (
              <CollapsibleFilterRow
                label="Capital"
                summary={backerGroups.length > 0 ? `${backerGroups.length} selected` : 'All'}
                active={backerGroups.length > 0}
              >
                <div className="cn-checklist">
                  {groupsPresent.map((g) => {
                    const on = backerGroups.includes(g);
                    return (
                      <button
                        key={g}
                        type="button"
                        role="checkbox"
                        aria-checked={on}
                        className={'cn-check' + (on ? ' on' : '')}
                        onClick={() => toggleBacker(g)}
                      >
                        <span className="cn-checkbox">{on ? '✓' : ''}</span>
                        <span className="cn-swatch" style={{ background: GROUP_COLOR[g] }} />
                        <span>{GROUP_LABEL[g] ?? g}</span>
                      </button>
                    );
                  })}
                </div>
              </CollapsibleFilterRow>
            )}

            {/* Revenue estimate (scale band) */}
            <CollapsibleFilterRow
              label="Revenue estimate"
              summary={`${scaleLabel(0)} – ${scaleLabel(1)}`}
              active={scale[0] > SCALE_MIN || scale[1] < SCALE_MAX}
            >
              <Slider.Root
                value={[idxOf(scale[0]), idxOf(scale[1])]}
                min={0}
                max={SCALE_STOPS.length - 1}
                step={1}
                onValueChange={(vs) => {
                  if (vs.length !== 2) return;
                  setScale([SCALE_STOPS[vs[0]!]!, SCALE_STOPS[vs[1]!]!]);
                }}
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  userSelect: 'none',
                  touchAction: 'none',
                  width: '100%',
                  height: 20,
                }}
              >
                <Slider.Track
                  style={{
                    position: 'relative',
                    flexGrow: 1,
                    background: 'var(--hairline)',
                    height: 3,
                    borderRadius: 2,
                  }}
                >
                  <Slider.Range
                    style={{ position: 'absolute', background: 'var(--accent)', height: '100%' }}
                  />
                </Slider.Track>
                {[0, 1].map((i) => (
                  <Slider.Thumb
                    key={i}
                    style={{
                      display: 'block',
                      width: 12,
                      height: 12,
                      background: 'var(--surface)',
                      border: '2px solid var(--accent)',
                      borderRadius: 8,
                      cursor: 'pointer',
                    }}
                  />
                ))}
              </Slider.Root>
            </CollapsibleFilterRow>

            {/* Sub-segment */}
            <CollapsibleFilterRow
              label="Sub-segment"
              summary={subsegments.length > 0 ? `${subsegments.length} selected` : 'All'}
              active={subsegments.length > 0}
            >
              <div className="cn-checklist">
                {CANONICAL_SUBSEGMENTS.map((s) => {
                  const on = subsegments.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      role="checkbox"
                      aria-checked={on}
                      className={'cn-check' + (on ? ' on' : '')}
                      onClick={() => toggleSub(s)}
                    >
                      <span className="cn-checkbox">{on ? '✓' : ''}</span>
                      <span>{s}</span>
                    </button>
                  );
                })}
              </div>
            </CollapsibleFilterRow>

            {/* Platform — searchable name picker */}
            {allGroups.length > 0 && (
              <CollapsibleFilterRow
                label="Platform"
                summary={groups.length > 0 ? `${groups.length} selected` : 'All'}
                active={groups.length > 0}
              >
                <div className="cn-searchbox">
                  <Search size={12} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                  <input
                    type="text"
                    placeholder="Search"
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      fontSize: 'var(--t-body)',
                      color: 'var(--text-primary)',
                    }}
                  />
                </div>
                <div className="cn-namelist">
                  {groupMatches.length === 0 ? (
                    <div className="cn-namenone">No match</div>
                  ) : (
                    groupMatches.map((g) => {
                      const on = groups.includes(g);
                      return (
                        <button
                          key={g}
                          type="button"
                          role="checkbox"
                          aria-checked={on}
                          className={'cn-check' + (on ? ' on' : '')}
                          onClick={() => toggleGroup(g)}
                        >
                          <span className="cn-checkbox">{on ? '✓' : ''}</span>
                          <span className="cn-nametxt">{g}</span>
                        </button>
                      );
                    })
                  )}
                </div>
              </CollapsibleFilterRow>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--s-2)',
              padding: 'var(--s-3)',
              borderTop: '1px solid var(--hairline)',
            }}
          >
            <button
              type="button"
              onClick={onReset}
              style={{
                fontSize: 'var(--t-body)',
                padding: '4px 12px',
                background: 'transparent',
                border: '1px solid var(--hairline)',
                borderRadius: 2,
                cursor: 'pointer',
                color: 'var(--text-secondary)',
              }}
            >
              Reset
            </button>
            <div style={{ flex: 1 }} />
            <Dialog.Close asChild>
              <button
                type="button"
                data-accent="true"
                style={{
                  fontSize: 'var(--t-body)',
                  fontWeight: 600,
                  padding: '4px 16px',
                  background: 'var(--accent)',
                  color: 'var(--bg)',
                  border: '1px solid var(--accent)',
                  borderRadius: 2,
                  cursor: 'pointer',
                }}
              >
                Done
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ---- scoped CSS — table tokens mirror the Screener -------------------------

const CN_CSS = `
.cn-root { display:flex; flex-direction:column; height:100%; font-size:var(--t-body); font-variant-numeric:tabular-nums; }
.cn-toolbar { display:flex; align-items:center; gap:var(--s-2); padding:var(--s-2) var(--s-3); border-bottom:1px solid var(--hairline); background:var(--surface); flex-wrap:wrap; }
.cn-seg { display:inline-flex; border:1px solid var(--hairline); border-radius:2px; overflow:hidden; background:var(--menu-surface); }
.cn-seg button { font:inherit; font-size:var(--t-body); font-weight:500; color:var(--text-secondary); background:transparent; border:none; border-right:1px solid var(--hairline); padding:3px 12px; cursor:pointer; white-space:nowrap; }
.cn-seg button:last-child { border-right:none; }
.cn-seg button.on { background:var(--accent); color:var(--bg); font-weight:600; }
.cn-seg button:not(.on):hover { background:var(--hover-subtle); }
.cn-filterbtn { display:inline-flex; align-items:center; gap:var(--s-1); padding:2px var(--s-2); font:inherit; font-size:var(--t-body); border:1px solid var(--hairline); border-radius:2px; background:var(--surface); color:var(--text-primary); cursor:pointer; }
.cn-filterbtn.active { background:var(--color-accent-100); }
.cn-filterbtn:hover { background:var(--hover-subtle); }
.cn-fcount { font-family:var(--font-mono); font-size:var(--t-meta); color:var(--accent); }
.cn-market { font-size:var(--t-meta); text-transform:uppercase; letter-spacing:.04em; color:var(--text-tertiary); font-weight:600; }
.cn-count { font-family:var(--font-mono); font-size:var(--t-meta); color:var(--text-tertiary); white-space:nowrap; }
.cn-tablewrap { flex:1; overflow:auto; min-height:0; }
.cn-empty { padding:var(--s-4); color:var(--text-tertiary); }
.cn-tbl { width:100%; border-collapse:collapse; }
.cn-tbl thead th { position:sticky; top:0; z-index:1; background:var(--surface-elevated); height:22px; text-align:left; font-size:var(--t-meta); text-transform:uppercase; letter-spacing:.04em; color:var(--text-secondary); font-weight:500; padding:0 10px; border-bottom:2px solid var(--text-primary); white-space:nowrap; }
.cn-tbl thead th.cn-sortable { cursor:pointer; }
.cn-tbl thead th.cn-sortable:hover { color:var(--text-primary); }
.cn-tbl thead th.on { color:var(--accent); font-weight:700; }
.cn-th-inner { display:inline-flex; align-items:center; gap:3px; }
.cn-tbl thead th.cn-r .cn-th-inner { flex-direction:row; }
.cn-tbl thead th.cn-r { text-align:right; }
.cn-tbl thead th.cn-r .cn-th-inner { justify-content:flex-end; }
.cn-tbl tbody td { padding:6px 10px; border-bottom:1px solid var(--hairline); vertical-align:middle; color:var(--text-primary); }
.cn-tbl tbody tr:hover { background:var(--hover-subtle); }
.cn-l { text-align:left; }
.cn-r { text-align:right; white-space:nowrap; font-family:var(--font-mono); }
.cn-name { font-weight:600; color:var(--text-primary); }
.cn-sponsor { color:var(--text-secondary); max-width:200px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.cn-rev { font-weight:600; }
.cn-dim { color:var(--text-tertiary); }
.cn-platnames { max-width:280px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; color:var(--text-secondary); font-size:var(--t-meta); }
.cn-subs { display:inline-flex; gap:4px; align-items:center; flex-wrap:wrap; }
.cn-subs-text { color:var(--text-secondary); line-height:1.35; }
.cn-chip { display:inline-block; font-size:10px; padding:1px 6px; border-radius:2px; border:1px solid var(--hairline); font-weight:500; color:var(--text-secondary); letter-spacing:.02em; white-space:nowrap; background:var(--menu-surface); }
.cn-chip-sub { color:var(--text-secondary); }
.cn-type { color:var(--text-secondary); }
.cn-foot { border-top:1px solid var(--hairline); padding:6px var(--s-3); font-size:var(--t-meta); color:var(--text-tertiary); line-height:1.4; }
/* map view — chrome (tiles/zoom/attribution) comes from .hc-map-root in map.css */
.cn-mapwrap { flex:1; position:relative; min-height:0; }
.cn-callout { position:absolute; top:10px; left:10px; z-index:1000; background:var(--surface); border:1px solid var(--hairline); border-radius:2px; padding:8px 12px; box-shadow:0 1px 5px rgba(26,20,16,.14); pointer-events:none; }
.cn-callout-n { font-size:22px; font-weight:700; line-height:1; letter-spacing:-0.01em; }
.cn-callout-k { font-size:var(--t-meta); color:var(--text-tertiary); margin-top:3px; }
.cn-wsdot { width:8px; height:8px; border-radius:2px; background:#8a5a3a; flex-shrink:0; }
.cn-maplegend { position:absolute; bottom:14px; left:10px; z-index:1000; background:color-mix(in srgb, var(--surface) 92%, transparent); border:1px solid var(--hairline); border-radius:2px; padding:6px 10px; display:flex; flex-wrap:wrap; align-items:center; gap:5px 12px; max-width:74%; box-shadow:0 1px 5px rgba(26,20,16,.14); }
.cn-legitem { display:inline-flex; align-items:center; gap:5px; font-size:var(--t-meta); color:var(--text-secondary); }
.cn-legsep { width:1px; align-self:stretch; background:var(--hairline); margin:0 2px; }
.cn-legsize { color:var(--text-tertiary); }
.cn-sizedot { display:inline-block; border-radius:50%; background:var(--text-tertiary); flex-shrink:0; }
.cn-gradbar { display:inline-block; width:40px; height:9px; border-radius:2px; border:1px solid rgba(26,20,16,.18); }
.cn-swatch { width:10px; height:10px; border-radius:50%; flex-shrink:0; display:inline-block; }
.cn-swatch-sq { border-radius:2px; }
.cn-maploading { position:absolute; top:10px; left:50%; transform:translateX(-50%); z-index:1000; background:var(--surface); border:1px solid var(--hairline); border-radius:4px; padding:4px 12px; font-size:var(--t-meta); color:var(--text-secondary); }
.cn-sitecard { position:absolute; top:10px; right:54px; z-index:1000; background:var(--surface); border:1px solid var(--hairline); border-radius:4px; padding:8px 26px 8px 12px; box-shadow:0 1px 5px rgba(26,20,16,.14); max-width:240px; }
.cn-sitecard-grp { font-size:var(--t-body); font-weight:600; color:var(--text-primary); line-height:1.2; }
.cn-sitecard-meta { font-size:var(--t-meta); color:var(--text-secondary); margin-top:3px; display:flex; align-items:center; gap:5px; flex-wrap:wrap; }
.cn-sitecard-x { position:absolute; top:5px; right:5px; width:18px; height:18px; display:inline-flex; align-items:center; justify-content:center; border:none; background:transparent; color:var(--text-tertiary); cursor:pointer; border-radius:2px; padding:0; }
.cn-sitecard-x:hover { background:var(--hover-subtle); color:var(--text-primary); }
/* filter modal */
.cn-fsection { margin-bottom:var(--s-3); }
.cn-flabel { font-size:var(--t-meta); text-transform:uppercase; letter-spacing:.05em; color:var(--text-tertiary); font-weight:600; margin-bottom:6px; display:flex; align-items:center; justify-content:space-between; }
.cn-fhint { font-family:var(--font-mono); text-transform:none; letter-spacing:0; color:var(--text-secondary); font-weight:500; }
.cn-soon { font-size:8px; text-transform:uppercase; letter-spacing:.05em; color:var(--text-tertiary); border:1px solid var(--hairline); border-radius:2px; padding:0 3px; margin-left:auto; }
.cn-searchbox { display:flex; align-items:center; gap:var(--s-1); padding:4px var(--s-2); background:var(--menu-surface); border:1px solid var(--hairline); border-radius:3px; margin-bottom:5px; }
.cn-searchbox:focus-within { border-color:var(--accent); }
.cn-namelist { max-height:148px; overflow-y:auto; display:flex; flex-direction:column; gap:1px; }
.cn-nametxt { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.cn-namenone { font-size:var(--t-meta); color:var(--text-tertiary); padding:6px; }
.cn-checklist { display:grid; grid-template-columns:1fr; gap:2px; }
.cn-check { display:flex; align-items:center; gap:7px; font:inherit; font-size:var(--t-body); padding:4px 6px; border:none; background:transparent; color:var(--text-primary); cursor:pointer; text-align:left; border-radius:2px; }
.cn-check:hover { background:var(--hover-subtle); }
.cn-check.on { color:var(--text-primary); }
.cn-check.soon { color:var(--text-tertiary); cursor:not-allowed; opacity:.55; }
.cn-check.soon:hover { background:transparent; }
.cn-checkbox { width:14px; height:14px; flex-shrink:0; border:1px solid var(--hairline); border-radius:2px; display:inline-flex; align-items:center; justify-content:center; font-size:10px; color:var(--bg); background:var(--surface); }
.cn-check.on .cn-checkbox { background:var(--accent); border-color:var(--accent); }
`;
